import React, { useState, useEffect, useRef } from "react";
import axios from "axios";

const API = process.env.REACT_APP_API_URL || "";

// Human-readable labels and ordering for each pipeline phase
const PHASE_META = {
  "mailbox:webhook":         { label: "MAILBOX — WEBHOOK",          order: 1 },
  "mailbox:asb":             { label: "MAILBOX — ASB CONSUMER",     order: 2 },
  "mailbox:poller":          { label: "MAILBOX — POLLER",           order: 3 },
  "mailbox:pipeline":        { label: "MAILBOX — PIPELINE",         order: 4 },
  "saga:email_ingestion":    { label: "SAGA — EMAIL INGESTION",     order: 5 },
  "saga:falcon_submission":  { label: "SAGA — FALCON SUBMISSION",   order: 6 },
};

// Lifecycle events that frame a phase — shown as separators, not steps
const LIFECYCLE_EVENTS = new Set([
  "pipeline_started", "pipeline_completed", "pipeline_failed", "pipeline_skipped",
]);

// ── Main component ────────────────────────────────────────────────────────────

function EventLogs({ onBack }) {
  const [searchText, setSearchText]   = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [data, setData]               = useState(null);
  const [loading, setLoading]         = useState(false);
  const [error, setError]             = useState("");
  const dropdownRef = useRef(null);

  // Load subject suggestions on mount
  useEffect(() => {
    axios
      .get(`${API}/api/eventlogs/subjects`, { withCredentials: true })
      .then(({ data: res }) => setSuggestions(res.subjects || []))
      .catch(() => {});
  }, []);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target))
        setShowDropdown(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleSearch = async (messageId) => {
    if (!messageId) return;
    setLoading(true);
    setError("");
    setData(null);
    setShowDropdown(false);
    try {
      const { data: result } = await axios.get(
        `${API}/api/eventlogs/${encodeURIComponent(messageId)}`,
        { withCredentials: true }
      );
      if (!result.total_events) setError("No events found for this message.");
      else setData(result);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to fetch event logs.");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => { e.preventDefault(); handleSearch(searchText.trim()); };
  const handleSelect = (item) => {
    setSearchText(item.subject || item.message_id);
    handleSearch(item.message_id);
  };

  const filtered = searchText.trim()
    ? suggestions.filter((s) =>
        (s.subject || "").toLowerCase().includes(searchText.toLowerCase()) ||
        (s.message_id || "").toLowerCase().includes(searchText.toLowerCase())
      )
    : suggestions;

  return (
    <div className="el-page">
      <div className="el-header">
        <button className="el-back-btn" onClick={onBack}>← Back</button>
        <h2>📋 Pipeline Event Logs</h2>
      </div>

      {/* Search bar */}
      <div className="el-search-wrapper" ref={dropdownRef}>
        <form onSubmit={handleSubmit} className="el-search-form">
          <input
            type="text"
            className="el-search-input"
            placeholder="Search by Message ID or Subject..."
            value={searchText}
            onChange={(e) => { setSearchText(e.target.value); setShowDropdown(true); }}
            onFocus={() => setShowDropdown(true)}
          />
          <button type="submit" className="el-search-btn" disabled={loading}>
            {loading ? "..." : "🔍"}
          </button>
        </form>
        {showDropdown && filtered.length > 0 && (
          <div className="el-dropdown">
            {filtered.slice(0, 15).map((item, i) => (
              <div key={i} className="el-dropdown-item" onClick={() => handleSelect(item)}>
                <span className="el-dropdown-subject">{item.subject || "No Subject"}</span>
                <span className="el-dropdown-date">{item.date || ""}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {error   && <div className="el-error">{error}</div>}
      {loading && <div className="el-loading">Loading...</div>}
      {data    && <PipelineTrace data={data} />}
    </div>
  );
}

// ── Pipeline trace view ───────────────────────────────────────────────────────

function PipelineTrace({ data }) {
  const { message_id, total_events, overall_outcome, mail, phases = [] } = data;

  // Sort phases by defined order
  const sorted = [...phases].sort((a, b) => {
    const oa = (PHASE_META[a.phase] || {}).order ?? 99;
    const ob = (PHASE_META[b.phase] || {}).order ?? 99;
    return oa - ob;
  });

  const outcomeIcon = overall_outcome === "failed"  ? "❌"
                    : overall_outcome === "skipped" ? "⏭"
                    : "✅";

  return (
    <div className="el-ascii-wrapper">
      <pre className="el-ascii">
        {/* ── Header ── */}
        <span className="el-ascii-header">
          {`Message ID : ${message_id}\n`}
          {`Total Events: ${total_events}  |  Outcome: ${outcomeIcon} ${overall_outcome.toUpperCase()}\n`}
        </span>

        {/* ── Mail context ── */}
        {mail && (
          <span className="el-ascii-mail">
            {`\nMail\n`}
            {`  Subject  : ${mail.subject || "—"}\n`}
            {`  Sender   : ${mail.sender || "—"}\n`}
            {`  Received : ${mail.received_datetime ? new Date(mail.received_datetime).toLocaleString() : "—"}\n`}
            {`  Attachments: ${mail.attachment_count ?? 0}\n`}
          </span>
        )}

        {`\n`}

        {/* ── Phases ── */}
        {sorted.map((phase, pi) => (
          <PhaseBlock key={phase.phase} phase={phase} index={pi} isLast={pi === sorted.length - 1} />
        ))}
      </pre>
    </div>
  );
}

// ── Phase block ───────────────────────────────────────────────────────────────

function PhaseBlock({ phase, index, isLast }) {
  const W = 80;
  const meta    = PHASE_META[phase.phase] || { label: phase.phase.toUpperCase() };
  const outcome = phase.outcome || "success";
  const outcomeTag = outcome === "failed"  ? "[FAILED]"
                   : outcome === "skipped" ? "[SKIPPED]"
                   : "[OK]";
  const isFailed = outcome === "failed";

  // Duration label
  const durLabel = phase.total_duration_ms > 0
    ? `${(phase.total_duration_ms / 1000).toFixed(1)}s`
    : "";

  // Header line content
  const headerContent = ` ${meta.label}`;
  const headerRight   = ` ${durLabel}  ${outcomeTag} `;
  const headerPad     = W - headerContent.length - headerRight.length;
  const headerLine    = `│${headerContent}${" ".repeat(Math.max(0, headerPad))}${headerRight}│`;

  // Step events only (exclude lifecycle wrappers)
  const steps = (phase.events || []).filter(e => !LIFECYCLE_EVENTS.has(e.event));

  // Pipeline started details (steps list)
  const startedEvent = (phase.events || []).find(e => e.event === "pipeline_started");
  const stepsList    = startedEvent?.details?.stages || startedEvent?.details?.steps || [];

  return (
    <>
      {index > 0 && <span>{`│\n▼\n`}</span>}

      {/* Box top */}
      <span className={isFailed ? "el-ascii-error" : "el-ascii-phase-ok"}>
        {`┌${"─".repeat(W)}┐\n`}
        {`${headerLine}\n`}
        {`├${"─".repeat(W)}┤\n`}
      </span>

      {/* Steps list from pipeline_started details */}
      {stepsList.length > 0 && (
        <span className="el-ascii-dim">
          {(() => {
            const prefix2 = `│  Steps: `;
            const full = stepsList.join(" → ");
            const lines2 = [];
            let cur = prefix2;
            for (const s of stepsList) {
              const add = (cur === prefix2 ? s : ` → ${s}`);
              if (cur.length + add.length > W) {
                lines2.push(cur.padEnd(W + 1) + "│");
                cur = `│         ${s}`;
              } else {
                cur += add;
              }
            }
            if (cur !== prefix2) lines2.push(cur.padEnd(W + 1) + "│");
            return lines2.map((l, i) => l + "\n").join("");
          })()}
          {`├${"─".repeat(W)}┤\n`}
        </span>
      )}

      {/* Step rows */}
      {steps.map((evt, si) => (
        <StepRow key={si} evt={evt} num={`${index + 1}.${si + 1}`} W={W} />
      ))}

      {/* Failed steps summary */}
      {phase.failed_steps && phase.failed_steps.length > 0 && (
        <>
          <span className="el-ascii-error">{`├${"─".repeat(W)}┤\n`}</span>
          {phase.failed_steps.map((fs, fi) => {
            const errMsg = fs.error?.message || fs.description || "Unknown error";
            const line   = `│  ✘ ${fs.stage}: ${errMsg}`;
            return (
              <span key={fi} className="el-ascii-error">
                {line.slice(0, W + 1).padEnd(W + 1) + "│\n"}
              </span>
            );
          })}
        </>
      )}

      {/* Box bottom */}
      <span className={isFailed ? "el-ascii-error" : "el-ascii-phase-ok"}>
        {`└${"─".repeat(W)}┘\n`}
      </span>
    </>
  );
}

// ── Step row ──────────────────────────────────────────────────────────────────

function StepRow({ evt, num, W }) {
  const stage     = evt.stage || evt.event || "UNKNOWN";
  const status    = (evt.status || "").toUpperCase();
  const isFailed  = status === "FAILED";
  const isSkipped = status === "SKIPPED";
  const durStr    = evt.duration_ms != null ? `${Math.round(evt.duration_ms)}ms` : "—";
  const statusTag = isFailed ? "[FAIL]" : isSkipped ? "[SKIP]" : "[OK]  ";
  const ts        = evt.created_at ? new Date(evt.created_at).toISOString().replace("T", " ").slice(0, 23) : "";

  // ── Main step line: num  STAGE  ···  timestamp  duration  status ──
  const prefix = `│  ${num.padEnd(5)} ${stage} `;
  const suffix = ` ${ts}  ${durStr.padStart(8)}  ${statusTag}  │`;
  const dots   = Math.max(0, W + 2 - prefix.length - suffix.length);
  const line   = `${prefix}${"·".repeat(dots)}${suffix}`;

  // ── Description line ─────────────────────────────────────────────
  const descLines = [];
  if (evt.description) {
    const raw = `│     │  ${evt.description}`;
    // wrap at W chars
    const words = evt.description.split(" ");
    let cur = `│     │  `;
    for (const w of words) {
      if (cur.length + w.length + 1 > W) {
        descLines.push(cur.padEnd(W + 1) + "│");
        cur = `│     │  ${w} `;
      } else {
        cur += w + " ";
      }
    }
    if (cur.trim() !== "│     │") descLines.push(cur.trimEnd().padEnd(W + 1) + "│");
  }

  // ── Details lines — one key: value per line, full values ─────────
  const detailLines = [];
  const details = evt.details;
  if (details && typeof details === "object") {
    const entries = Object.entries(details).filter(([, v]) => {
      if (v === null || v === undefined || v === "") return false;
      if (Array.isArray(v) && v.length === 0) return false;
      if (typeof v === "object" && !Array.isArray(v) && Object.keys(v).length === 0) return false;
      return true;
    });
    entries.forEach(([k, v], idx) => {
      const isLast = idx === entries.length - 1;
      const connector = isLast ? "└──" : "├──";
      let valStr;
      if (Array.isArray(v)) {
        valStr = v.length <= 5
          ? `[${v.join(", ")}]`
          : `[${v.slice(0, 5).join(", ")} … +${v.length - 5} more]`;
      } else if (typeof v === "object") {
        valStr = JSON.stringify(v);
      } else {
        valStr = String(v);
      }
      const prefix2 = `│     ${connector} ${k}: `;
      // wrap long values
      const maxVal = W - prefix2.length;
      if (valStr.length <= maxVal) {
        detailLines.push((prefix2 + valStr).padEnd(W + 1) + "│");
      } else {
        // first chunk
        detailLines.push((prefix2 + valStr.slice(0, maxVal)).padEnd(W + 1) + "│");
        // continuation lines
        let rest = valStr.slice(maxVal);
        const contPrefix = `│     ${" ".repeat(connector.length + 1)}${" ".repeat(k.length + 2)}`;
        while (rest.length > 0) {
          const chunk = rest.slice(0, W - contPrefix.length);
          detailLines.push((contPrefix + chunk).padEnd(W + 1) + "│");
          rest = rest.slice(chunk.length);
        }
      }
    });
  }

  // ── Error lines ───────────────────────────────────────────────────
  const errorLines = [];
  if (evt.error) {
    const code = evt.error.code || "ERROR";
    const msg  = evt.error.message || "";
    const retryable = evt.error.retryable != null ? ` | retryable=${evt.error.retryable}` : "";
    errorLines.push((`│     ├── ✘ [${code}]${retryable}: ${msg}`).slice(0, W + 1).padEnd(W + 1) + "│");
    if (evt.error.stack_trace) {
      const lines = evt.error.stack_trace.split("\n").filter(Boolean).slice(0, 8);
      lines.forEach((l, li) => {
        const conn = li === lines.length - 1 ? "└──" : "│  ";
        errorLines.push((`│     ${conn}   ${l.trim()}`).slice(0, W + 1).padEnd(W + 1) + "│");
      });
    }
  }

  const rowClass = isFailed ? "el-ascii-error" : isSkipped ? "el-ascii-dim" : "";

  return (
    <>
      <span className={rowClass}>{line + "\n"}</span>
      {descLines.map((l, i)    => <span key={`d${i}`}  className="el-ascii-desc">{l + "\n"}</span>)}
      {detailLines.map((l, i)  => <span key={`kv${i}`} className="el-ascii-detail">{l + "\n"}</span>)}
      {errorLines.map((l, i)   => <span key={`e${i}`}  className="el-ascii-error">{l + "\n"}</span>)}
    </>
  );
}

export default EventLogs;
